import { useState, useEffect } from 'react';
import supabaseClient from './supabaseClient';
import primaryDomainQuotesData from '../_constants/primaryDomainQuotes.json';
const { primary_domain_quotes } = primaryDomainQuotesData;

export interface SubdomainRecommendation {
  id: number;
  name: string;
  current_phase: string;
  goal_phase: string;
  your_path: string;
  selected_level: number;
  goal_level: number;
}

export interface DomainRecommendationData {
  id: number;
  name: string;
  score: number;
  definition: string;
  quote: string;
  subdomains: SubdomainRecommendation[];
}

interface UseDomainRecommendationsReturn {
  data: DomainRecommendationData | null;
  loading: boolean;
  error: string | null;
}

const PHASE_NAMES = {
  0: 'Not Applicable',
  1: 'Discovery',
  2: 'Foundational', 
  3: 'Emerging',
  4: 'Developing',
  5: 'Advancing',
  6: 'Thriving'
};


const getGoalPhase = (currentLevel: number): number => {
  if (currentLevel <= 0) return 1;
  if (currentLevel >= 6) return 6;
  return currentLevel + 1;
};

export const useDomainRecommendations = (
  domainId: string | undefined, 
  assessmentId: string | undefined
): UseDomainRecommendationsReturn => {
  const [data, setData] = useState<DomainRecommendationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!domainId || !assessmentId) return;

    const fetchDomainRecommendations = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: domainInfo, error: domainError } = await supabaseClient
          .from('domains')
          .select('id, name')
          .eq('id', domainId)
          .maybeSingle();

        if (domainError) {
          throw new Error(`Error fetching domain: ${domainError.message}`);
        }

        if (!domainInfo) {
          throw new Error('Domain not found');
        }

        const { data: domainResults, error: resultsError } = await supabaseClient
          .from('user_assessment_results')
          .select('*')
          .eq('assessment_id', assessmentId)
          .eq('domain_id', domainId)
          .not('selected_level', 'is', null);

        if (resultsError) {
          throw new Error(`Error fetching domain results: ${resultsError.message}`);
        }

        if (!domainResults || domainResults.length === 0) {
          throw new Error('No assessment results found for this domain');
        }

        const totalScoreAll = domainResults.reduce((sum, result) => sum + (result.score_percentage || 0), 0);
        const averageScore = Math.round(totalScoreAll / domainResults.length);

        const baseResults = domainResults;

        const subdomains: SubdomainRecommendation[] = baseResults
          .map((result) => {
            const currentLevel = result.selected_level || 0;
            const goalLevel = getGoalPhase(currentLevel);
            
            return {
              id: result.subdomain_id,
              name: result.subdomain_name,
              current_phase: PHASE_NAMES[currentLevel as keyof typeof PHASE_NAMES] || 'Unknown',
              goal_phase: PHASE_NAMES[goalLevel as keyof typeof PHASE_NAMES] || 'Unknown',
              your_path: 'Click here',
              selected_level: currentLevel,
              goal_level: goalLevel
            };
          });

    const domainDefinitions: Record<string, { definition: string; quote: string }> = {
          'Organizational Infrastructure': {
            definition: primary_domain_quotes.organizational_infrastructure.definition,
            quote: primary_domain_quotes.organizational_infrastructure.quote
          },
          'Board Governance': {
            definition: primaryDomainQuotesData.primary_domain_quotes.board_governance.definition,
            quote: primary_domain_quotes.board_governance.quote
          },
          'Programs and Services': {
            definition: primaryDomainQuotesData.primary_domain_quotes.programs_services.definition,
            quote: primary_domain_quotes.programs_services.quote
          },
          'Community Partnerships': {
            definition: primaryDomainQuotesData.primary_domain_quotes.community_partnerships.definition,
            quote: primary_domain_quotes.community_partnerships.quote
          },
          'Resource & Fund Development': {
            definition: primaryDomainQuotesData.primary_domain_quotes.resource_fund_development.definition,
            quote: primary_domain_quotes.resource_fund_development.quote
          },
          'Fiscal Management': {
            definition: primary_domain_quotes.fiscal_management.definition,
            quote: primary_domain_quotes.fiscal_management.quote
          },
          'Data Systems & Technology': {
            definition: primaryDomainQuotesData.primary_domain_quotes.data_systems_technology.definition,
            quote: primary_domain_quotes.data_systems_technology.quote
          },
          'Program Development': {
            definition: primary_domain_quotes.programs_services.definition,
            quote: primary_domain_quotes.programs_services.quote
          },
          'Staff Management': {
            definition: primaryDomainQuotesData.primary_domain_quotes.staff_management.definition,
            quote: primary_domain_quotes.staff_management.quote
          },
          'Evaluation': {
            definition: primaryDomainQuotesData.primary_domain_quotes.evaluation.definition,
            quote: primary_domain_quotes.evaluation.quote
          },
        };

        const domainMeta = domainDefinitions?.[domainInfo.name];


        setData({
          id: parseInt(domainId),
          name: domainInfo.name,
          score: averageScore,
          definition: domainMeta.definition,
          quote: domainMeta.quote,
          subdomains
        });

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        console.error('Error fetching domain recommendations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDomainRecommendations();
  }, [domainId, assessmentId]);

  return { data, loading, error };
}; 